import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    input,
    output,
    signal,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import { Report } from "../../../pojo/Report";
import { S25Util } from "../../../util/s25-util";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Merge, Nullable } from "../../../pojo/Util";
import { EventService } from "../../../services/event.service";
import { Throttle } from "../../../decorators/debounce.decorator";
import { Doc } from "./s25.document.const";
import { Bind } from "../../../decorators/bind.decorator";
import { s25Resource } from "../../../util/s25.resource";
import { PricingService } from "../../../services/pricing.service";

@Component({
    standalone: false,
    selector: "s25-ng-report-parameters",
    template: `
        @for (parameter of parameters(); track parameter) {
            <div class="parameter" [attr.data-type]="parameter.parameter_type">
                <ng-template #label>
                    <label>
                        {{ parameter.parameter_name }}
                        @if (parameter.required === "F") {
                            <span class="small">(optional)</span>
                        }
                    </label>
                </ng-template>
                @if (parameter.hasLabel) {
                    <ng-container [ngTemplateOutlet]="label" />
                }
                @switch (parameter.parameter_type) {
                    @case (Report.parameterType.String) {
                        <input class="c-input" type="text" [(ngModel)]="parameter.value" />
                    }
                    @case (Report.parameterType.Integer) {
                        <input class="c-input" type="text" [(ngModel)]="parameter.value" />
                    }
                    @case (Report.parameterType.Float) {
                        <input class="c-input" type="text" [(ngModel)]="parameter.value" />
                    }
                    @case (Report.parameterType.Time) {
                        <input class="c-input" type="text" [(ngModel)]="parameter.value" />
                    }
                    @case (Report.parameterType.DateTime) {
                        <input class="c-input" type="text" [(ngModel)]="parameter.value" />
                    }
                    @case (Report.parameterType.Object) {
                        <input class="c-input" type="text" [(ngModel)]="parameter.value" />
                    }
                    @case (Report.parameterType.MeetingPatterns) {
                        <input class="c-input" type="text" [(ngModel)]="parameter.value" />
                    }
                    @case (Report.parameterType.Date) {
                        <s25-datepicker
                            [value]="parameter.value()"
                            (valueChange)="parameter.value.set(S25Util.date.toS25ISODateStr($event)!)"
                            [minDate]="parameter === endDtParam() ? startDt() : undefined"
                            [autoSelectToday]="!parameter.value()"
                        />
                    }
                    @case (Report.parameterType.Boolean) {
                        <ng-container [ngTemplateOutlet]="label" />
                        <s25-toggle-button
                            [value]="parameter.value()"
                            (valueChange)="parameter.value.set($event ? 'T' : 'F')"
                            [trueLabel]="'Yes'"
                            [falseLabel]="'No'"
                        />
                    }
                    @case (Report.parameterType.EventSearch) {
                        <ng-container *ngTemplateOutlet="search; context: { type: 'event', parameter: parameter }" />
                    }
                    @case (Report.parameterType.OrganizationSearch) {
                        <ng-container
                            *ngTemplateOutlet="search; context: { type: 'organization', parameter: parameter }"
                        />
                    }
                    @case (Report.parameterType.LocationSearch) {
                        <ng-container *ngTemplateOutlet="search; context: { type: 'location', parameter: parameter }" />
                    }
                    @case (Report.parameterType.ResourceSearch) {
                        <ng-container *ngTemplateOutlet="search; context: { type: 'resource', parameter: parameter }" />
                    }
                    @case (Report.parameterType.GroupBy) {
                        <select class="cn-form__control" [(ngModel)]="parameter.value">
                            @for (item of parameter.options; track item.itemId) {
                                <option [ngValue]="item.itemId">{{ item.itemName }}</option>
                            }
                        </select>
                    }
                    @case ("locator") {
                        <input
                            [ngModel]="undefined"
                            (ngModelChange)="getEventIdFromLocator(parameter, $event)"
                            class="c-input event-locator"
                            placeholder="YYYY-ABCDEF"
                        />
                    }
                    @case ("occurrence") {
                        @if (!!locatorParam()?.value()) {
                            <ng-container [ngTemplateOutlet]="label" />
                            <s25-ng-document-reservation-list
                                [eventId]="$any($safeNavigationMigration(locatorParam()?.value()))"
                                (selected)="parameter.value.set($event.toString())"
                            />
                        }
                    }
                    @case ("invoice") {
                        @if (!!locatorParam()?.value()) {
                            <ng-container [ngTemplateOutlet]="label" />
                            <s25-ng-document-invoice-list
                                [eventId]="$any($safeNavigationMigration(locatorParam()?.value()))"
                                (selected)="parameter.value.set($event)"
                            />
                        }
                    }
                    @case ("organization") {
                        @if (!!invoiceParam()) {
                            @if (!!invoiceParam().value()) {
                                <ng-container [ngTemplateOutlet]="label" />
                            }
                            @if (invoiceOrgs.resource.isLoading()) {
                                <s25-ng-loading-inline-static />
                            } @else if (invoiceOrgs.resource.error()) {
                                <span>Unable to load organizations for invoice</span>
                            } @else if (!!invoiceOrgs.value()) {
                                <select class="cn-form__control" [(ngModel)]="parameter.value">
                                    <option [ngValue]="undefined">None</option>
                                    @for (org of invoiceOrgs.value(); track org.id) {
                                        <option [ngValue]="org.id">{{ org.name }}</option>
                                    }
                                </select>
                            }
                        } @else {
                            <ng-container [ngTemplateOutlet]="label" />
                            <s25-ng-dropdown-search-criteria
                                [type]="'organizations'"
                                [value]="{ itemId: parameter.value() }"
                                (valueChange)="parameter.value.set($event.itemId)"
                            />
                        }
                    }
                }
                @if (parameter.loading()) {
                    <s25-ng-loading-inline-static [text]="null" />
                }
            </div>
        }

        <ng-template #search let-type="type" let-parameter="parameter">
            <s25-ng-search-dropdown
                [type]="type"
                [allowNonQueryId]="false"
                (valueChange)="parameter.value.set($safeNavigationMigration($event?.itemId))"
                [hasNoneItem]="parameter.required === 'F'"
            />
        </ng-template>
    `,
    styles: `
        :host {
            display: grid;
            grid-template-columns: auto auto 2em 1fr;
            grid-row-gap: 0.5rem;
            grid-column-gap: 1rem;
        }

        .parameter {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: 1 / -1;
            align-items: center;

            &[data-type="occurrence"],
            &[data-type="invoice"] {
                grid-template-columns: 1fr;
            }
        }

        input.event-locator {
            width: 13ch;
            box-sizing: content-box;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class S25ReportParametersComponent {
    readonly rawParams = input.required<Report.Parameter[]>({ alias: "parameters" });
    readonly valuesChange = output<Record<Report.Parameter["parameter_field_mapping"], Report.ParameterValue>>({});

    readonly parameters = computed(() =>
        this.rawParams()
            .filter((param) => Report.validParameterTypes.has(param.parameter_type))
            .map(this.mapParameter),
    );

    readonly startDt = computed(() => {
        const param = this.parameters().find((p) => /start date/i.test(p.parameter_name));
        const value = param?.value();
        return S25Util.date.isDate(value) ? value : undefined;
    });
    readonly endDtParam = computed(() => this.parameters().find((p) => /end date/i.test(p.parameter_name)));
    readonly locatorParam = computed(() => this.parameters().find((p) => p.parameter_type === "locator"));
    readonly invoiceParam = computed(() => this.parameters().find((p) => p.parameter_type === "invoice"));
    readonly orgParam = computed(() => this.parameters().find((p) => p.parameter_type === "organization"));

    readonly invoiceOrgs = s25Resource({
        params: () => <number>this.invoiceParam()?.value() ?? undefined,
        loader: async ({ params: invoiceId, abortSignal }) => {
            const invoice = await PricingService.getInvoice(invoiceId, { include: ["organizations"], expand: true });
            const orgs = invoice.expandedInfo.organizations?.map((org) => ({
                id: org.organizationId,
                name: org.organizationName,
            }));
            return orgs ?? [];
        },
    });

    protected readonly Report = Report;
    protected readonly S25Util = S25Util;

    groupByMap: Record<string, string> = {
        "Reservation Start": "Occurrence Start",
        "Reservation Dates": "Occurrence Dates",
    };

    constructor() {
        effect(this.onChange);

        effect(() => {
            // Update selected org when invoice orgs load
            const orgParam = this.orgParam();
            if (!this.invoiceParam() || !orgParam) return;
            const selectedOrg = this.invoiceOrgs.lastValue()?.find((org) => org.id === orgParam.value());
            orgParam.value.set(selectedOrg?.id);
        });
    }

    @Bind
    mapParameter(param: Report.Parameter) {
        const newParam: Parameter = {
            ...param,
            options: [] as DropDownItem[],
            value: signal(this.defaultValue(param)),
            loading: signal(false),
            hasLabel: true,
            parameter_name: Doc.parameterNameMap[param.parameter_name] ?? param.parameter_name,
        };

        if (param.validate_type === Report.parameterValidateType.GroupBy) {
            newParam.options = param
                .validate!.split(",")
                .map((val) => ({ itemId: val, itemName: this.groupByMap[val] ?? val }));
        }

        if (param.parameter_type === Report.parameterType.Boolean) {
            newParam.hasLabel = false;
        } else if (param.parameter_type === Report.parameterType.Integer && /EVENT.ID/i.test(param.parameter_name)) {
            newParam.parameter_type = "locator";
        } else if (param.parameter_type === Report.parameterType.String && /RSRV.ID/i.test(param.parameter_name)) {
            newParam.parameter_type = "occurrence";
            newParam.hasLabel = false;
        } else if (param.parameter_type === Report.parameterType.Integer && /Bill.ID/i.test(param.parameter_name)) {
            newParam.parameter_type = "invoice";
            newParam.hasLabel = false;
        } else if (param.parameter_type === Report.parameterType.Integer && /ORG.ID/i.test(param.parameter_name)) {
            newParam.parameter_type = "organization";
            newParam.hasLabel = false;
        }

        return newParam;
    }

    defaultValue(param: Report.Parameter): Nullable<Report.ParameterValue> {
        if (param.parameter_type === Report.parameterType.Date) return S25Util.date.toS25ISODateStr(new Date())!;
        if (param.validate_type === Report.parameterValidateType.GroupBy) return param.validate!.split(",")[0];
        if (param.parameter_type === Report.parameterType.Boolean) return "F";
        return undefined;
    }

    @Bind
    onChange() {
        const newValues = Object.fromEntries(
            this.parameters().map((param) => [param.parameter_field_mapping, param.value()]),
        );
        this.valuesChange.emit(newValues);
    }

    @Throttle(500)
    async getEventIdFromLocator(parameter: Parameter, locator: string) {
        parameter.value.set(undefined);
        if (!S25Util.isEventLocator(locator)) return;
        parameter.loading.set(true);
        const [eventId, err] = await S25Util.Maybe(EventService.getEventIdByLocator(locator));
        parameter.loading.set(false);
        if (!eventId || !!err) {
            alert("Failed to load event, please make sure your event reference is correct.");
            return;
        }
        parameter.value.set(eventId);
    }
}

type Parameter = Merge<
    Report.Parameter,
    {
        options: DropDownItem[];
        value: WritableSignal<Nullable<Report.ParameterValue>>;
        parameter_type: Report.ParameterType | "locator" | "occurrence" | "invoice" | "organization";
        loading: WritableSignal<boolean>;
        hasLabel: boolean;
    }
>;
